/**
 * Fallback Manager
 * Handles fallback UI state for journey generation failures
 */

import StartAgain from './start-again.js';
import ParentMessenger from './parent-messenger.js';

// Fallback reasons constants
export const FALLBACK_REASONS = {
  API_REQUEST_FAILED: 'api_request_failed',
  DIAGRAM_GENERATION_FAILED: 'diagram_generation_failed',
  TIPS_GENERATION_FAILED: 'tips_generation_failed',
  BOTH_GENERATION_FAILED: 'both_generation_failed'
} as const;

export type FallbackReason = typeof FALLBACK_REASONS[keyof typeof FALLBACK_REASONS];

interface ErrorDetails {
  status?: number;
  error?: string;
  journeyId?: string;
  isTestMode?: boolean;
  [key: string]: unknown;
}

class FallbackManager {
  private fallbackContainer: HTMLElement | null = null;
  private embedPageContainer: HTMLElement | null = null;
  private resultsContainer: HTMLElement | null = null;
  private startAgainButton: HTMLElement | null = null;
  private isInitialized: boolean = false;
  
  // Allow injection of navigation function for testing
  private navigateTo: (url: string) => void = (url) => {
    window.location.href = url;
  };

  /**
   * Initialize the fallback manager
   */
  initialize(): void {
    if (this.isInitialized) return;

    // Get DOM elements
    this.fallbackContainer = document.getElementById('fallbackContainer');
    this.embedPageContainer = document.getElementById('embedPageContainer');
    this.resultsContainer = document.getElementById('resultsContainer');
    this.startAgainButton = document.getElementById('startAgainButton');

    // Check URL parameter on initialization
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('showFallback') === 'true') {
      this.showFallback(FALLBACK_REASONS.API_REQUEST_FAILED, {
        isTestMode: true
      });
    }

    // Set up Start Again button
    if (this.startAgainButton) {
      this.startAgainButton.addEventListener('click', () => this.startAgain());
    }

    this.isInitialized = true;
  }

  /**
   * Show the fallback UI
   */
  showFallback(reason: FallbackReason, errorDetails: ErrorDetails = {}): void {
    if (!this.fallbackContainer) {
      console.error('Fallback container not found');
      return;
    }

    console.log('Showing fallback UI', { reason, errorDetails });

    // Store error context in StateManager if available
    if (window.stateManager) {
      window.stateManager.setState('fallback.active', true);
      window.stateManager.setState('fallback.reason', reason);
      window.stateManager.setState('fallback.errorDetails', errorDetails);
      window.stateManager.setState('fallback.timestamp', Date.now());
    }

    // Hide other containers
    if (this.embedPageContainer) {
      this.embedPageContainer.style.display = 'none';
    }
    if (this.resultsContainer) {
      this.resultsContainer.style.display = 'none';
    }

    // Show fallback container
    this.fallbackContainer.classList.remove('hidden');
    
    // Notify parent that fallback is shown (for mobile scroll)
    ParentMessenger.send('FALLBACK_SHOWN', {});
  }

  /**
   * Hide the fallback UI
   */
  hideFallback(): void {
    if (!this.fallbackContainer) return;

    console.log('Hiding fallback UI');

    // Update state
    if (window.stateManager) {
      window.stateManager.setState('fallback.active', false);
    }

    // Hide fallback container
    this.fallbackContainer.classList.add('hidden');
  }

  /**
   * Reset the journey builder to initial state
   */
  startAgain(): void {
    // Get any stored form data from the error details
    const errorDetails = this.getErrorDetails();
    let prefillData: { company?: string; product?: string; email?: string } | undefined;
    
    if (errorDetails && errorDetails.formData && typeof errorDetails.formData === 'object') {
      const formData = errorDetails.formData as any;
      prefillData = {
        company: formData.company,
        product: formData.product,
        email: formData.email
      };
    }
    
    StartAgain.execute(prefillData);
  }

  /**
   * Check if fallback is currently active
   */
  isActive(): boolean {
    return window.stateManager ? 
      window.stateManager.getState('fallback.active') === true : 
      !this.fallbackContainer?.classList.contains('hidden');
  }

  /**
   * Get the current fallback reason
   */
  getReason(): FallbackReason | null {
    if (!window.stateManager) return null;
    const reason = window.stateManager.getState('fallback.reason');
    return reason !== undefined ? reason as FallbackReason : null;
  }

  /**
   * Get error details
   */
  getErrorDetails(): ErrorDetails | null {
    if (!window.stateManager) return null;
    const details = window.stateManager.getState('fallback.errorDetails');
    return details !== undefined ? details as ErrorDetails : null;
  }

  /**
   * Set navigation function (for testing)
   * @internal
   */
  _setNavigationFunction(navFn: (url: string) => void): void {
    this.navigateTo = navFn;
  }
}

// Create singleton instance
const fallbackManager = new FallbackManager();

// Export for use in other modules
export { fallbackManager };

// Also expose on window for non-module scripts
declare global {
  interface Window {
    fallbackManager: FallbackManager;
    FALLBACK_REASONS: typeof FALLBACK_REASONS;
    stateManager?: {
      getState: (path?: string) => unknown;
      setState: (path: string, value: unknown) => unknown;
      subscribe: (path: string, callback: (newValue: unknown, oldValue: unknown) => void) => () => void;
      resetState: (newInitialState?: unknown) => unknown;
    };
  }
}

window.fallbackManager = fallbackManager;
window.FALLBACK_REASONS = FALLBACK_REASONS;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => fallbackManager.initialize());
} else {
  fallbackManager.initialize();
}